import {
  groupTimelineEvents,
  type TimelineItem as TimelineItemData,
} from './groupTimelineEvents';
import { TaskOperatorEventGroup } from './TaskOperatorEventGroup';
import { TIMELINE_KIND_ICONS as KIND_ICONS } from './taskTimelineIcons';
import type { ReactNode } from 'react';
import { T, useGT, Var } from 'gt-react';
import { Link } from '@tanstack/react-router';
import { format, formatDistanceToNow } from 'date-fns';
import {
  ArrowClockwiseIcon,
  ArrowRightIcon,
  FlowArrowIcon,
  GearSixIcon,
  RobotIcon,
  UserIcon,
} from '@phosphor-icons/react';
import type { TaskTimelineActor, TaskTimelineEntry } from '@erp/rpc';
import {
  OUTCOME_VALIDATION_FAILED_ACTION_KEY,
  AGENT_HELP_REQUESTED_ACTION_KEY,
  HUMAN_INPUT_PROVIDED_ACTION_KEY,
  PROCEDURE_LOCK_RELEASED_ACTION_KEY,
  TASK_RETURNED_TO_AGENT_ACTION_KEY,
} from '@erp/shared-types';
import { TaskOutcomeDataDisplay } from './TaskOutcomeFields';
import { Button } from 'src/components/ui/button';
import { Card } from 'src/components/ui/card';
import { TaskTimelineViewSwitch } from './TaskTimelineViewSwitch';
import { recordedTimelineAgentName } from './taskRunPresentation';
import { TaskRunOutcomeBadge, TaskRunSummary } from './TaskRunSummary';
import { TaskRunEvidenceButton } from './TaskRunEvidenceButton';
import { cn } from '@/lib/utils';
import {
  describeHumanAction,
  type HumanActionDetailView,
} from './humanActionPayload';

/** Relative label in the viewer's local timezone (date-fns default). */
function formatTimelineRelative(date: Date): string {
  return formatDistanceToNow(date, { addSuffix: true });
}

/** Absolute local timestamp for hover / screen-reader precision. */
function formatTimelineAbsolute(date: Date): string {
  return format(date, 'MMM d, yyyy · h:mm a');
}

/**
 * Unified audit timeline for a task and its subtask subtree — renders every
 * `kind` of `tasks.getTimeline` entry with actor iconography and load-more
 * pagination. Newest-first facts only: the timeline is a query over real
 * records, never a log the task maintains itself.
 */
function TaskTimelineBody({
  error,
  isLoading,
  entries,
  items,
  canInspectRuns,
  rootTaskId,
  userNameById,
  agentNameById,
  hasMore,
}: {
  error?: unknown;
  isLoading?: boolean;
  entries: TaskTimelineEntry[];
  items: ReturnType<typeof groupTimelineEvents>;
  canInspectRuns: boolean;
  rootTaskId: string;
  userNameById: Map<string, string>;
  agentNameById: Map<string, string>;
  hasMore?: boolean;
}) {
  return (
    <>
      {Boolean(error) && (
        <p role="alert" className="mb-2 text-xs text-destructive">
          <T context="task timeline query error">
            Activity could not be refreshed. Please try again.
          </T>
        </p>
      )}
      {isLoading ? (
        <div className="py-6 text-center text-xs text-muted-foreground">
          <T context="loading state for the task audit timeline">Loading…</T>
        </div>
      ) : entries.length === 0 ? (
        <div className="py-6 text-center text-xs text-muted-foreground">
          <T context="empty state for the task audit timeline">
            No activity recorded yet.
          </T>
        </div>
      ) : (
        <ol className="flex min-h-0 flex-1 flex-col gap-0 overflow-y-auto">
          {items.map((item, index) => (
            <TimelineItem
              key={item.kind === 'entry' ? item.entry.id : item.entries[0]?.id}
              item={item}
              canInspectRuns={canInspectRuns}
              rootTaskId={rootTaskId}
              isLast={index === items.length - 1 && !hasMore}
              userNameById={userNameById}
              agentNameById={agentNameById}
            />
          ))}
        </ol>
      )}
    </>
  );
}

export function TaskTimeline({
  entries,
  rootTaskId,
  userNameById,
  agentNameById,
  isLoading,
  hasMore,
  isFetchingMore,
  onLoadMore,
  className,
  view,
  onViewChange,
  onRefresh,
  canInspectRuns = false,
  error,
  showHeading = true,
}: {
  onRefresh?: () => void;
  view?: 'summary' | 'events';
  onViewChange?: (view: 'summary' | 'events') => void;
  canInspectRuns?: boolean;
  error?: unknown;
  entries: TaskTimelineEntry[];
  rootTaskId: string;
  userNameById: Map<string, string>;
  agentNameById: Map<string, string>;
  isLoading?: boolean;
  hasMore?: boolean;
  isFetchingMore?: boolean;
  onLoadMore?: () => void;
  className?: string;
  /** Hosts that already label the panel (a tab or rail heading) pass false. */
  showHeading?: boolean;
}) {
  const items = groupTimelineEvents(entries, view);
  return (
    <Card
      className={cn('flex min-h-0 flex-col overflow-hidden p-3', className)}
      data-testid="task-timeline"
    >
      {showHeading && (
        <div className="mb-2 shrink-0 text-[11px] font-semibold uppercase tracking-[0.06em] text-stone-500">
          <T context="heading for the task audit timeline, distinct from an embedded record's activity">
            Task Activity
          </T>
        </div>
      )}
      <div className="flex items-start justify-between gap-2">
        <TaskTimelineViewSwitch view={view} onChange={onViewChange} />
        {onRefresh && (
          <Button variant="ghost" size="sm" onClick={onRefresh}>
            <ArrowClockwiseIcon data-icon="inline-start" />
            <T context="refresh task audit timeline button">Refresh</T>
          </Button>
        )}
      </div>
      <TaskTimelineBody
        error={error}
        isLoading={isLoading}
        entries={entries}
        items={items}
        canInspectRuns={canInspectRuns}
        rootTaskId={rootTaskId}
        userNameById={userNameById}
        agentNameById={agentNameById}
        hasMore={hasMore}
      />
      <LoadOlderEvents
        hasMore={hasMore}
        isFetching={isFetchingMore}
        onLoadMore={onLoadMore}
      />
    </Card>
  );
}

function LoadOlderEvents({
  hasMore,
  isFetching,
  onLoadMore,
}: {
  hasMore?: boolean;
  isFetching?: boolean;
  onLoadMore?: () => void;
}) {
  const gt = useGT();
  if (!hasMore) return null;
  return (
    <Button
      variant="ghost"
      size="sm"
      className="mt-1 shrink-0 self-center text-xs"
      disabled={isFetching}
      onClick={onLoadMore}
    >
      {isFetching
        ? gt('Loading…', { context: 'timeline load-more, in flight' })
        : gt('Load older', {
            context: 'button, load older task timeline entries',
          })}
    </Button>
  );
}

function actorLabel(
  actor: TaskTimelineActor,
  userNameById: Map<string, string>,
  agentNameById: Map<string, string>,
  gt: ReturnType<typeof useGT>,
): string {
  switch (actor.type) {
    case 'user':
      return (
        userNameById.get(actor.userId) ??
        gt('User', { context: 'timeline actor fallback label' })
      );
    case 'agent':
      return (
        (actor.agentDefinitionId
          ? agentNameById.get(actor.agentDefinitionId)
          : undefined) ??
        gt('Agent', { context: 'timeline actor fallback label' })
      );
    case 'workflow':
      return actor.workflowName
        ? gt('Workflow {name}', {
            context: 'timeline actor label, a named workflow run',
            name: actor.workflowName,
          })
        : gt('Workflow', { context: 'timeline actor fallback label' });
    case 'system':
      return gt('System', { context: 'timeline actor label' });
  }
}

function ActorIcon({ actor }: { actor: TaskTimelineActor }) {
  const className = 'size-3.5';
  switch (actor.type) {
    case 'user':
      return <UserIcon className={className} weight="fill" />;
    case 'agent':
      return <RobotIcon className={className} weight="fill" />;
    case 'workflow':
      return <FlowArrowIcon className={className} weight="bold" />;
    case 'system':
      return <GearSixIcon className={className} />;
  }
}

function TimelineItem({
  item,
  rootTaskId,
  ...props
}: {
  item: TimelineItemData;
  rootTaskId: string;
  canInspectRuns: boolean;
  isLast: boolean;
  userNameById: Map<string, string>;
  agentNameById: Map<string, string>;
}) {
  if (item.kind === 'operator_group')
    return (
      <TaskOperatorEventGroup
        entries={item.entries}
        rootTaskId={rootTaskId}
        canInspectRuns={props.canInspectRuns}
        isLast={props.isLast}
        agentNameById={props.agentNameById}
      />
    );
  return (
    <TimelineRow
      entry={item.entry}
      isSubtaskEntry={item.entry.taskId !== rootTaskId}
      {...props}
    />
  );
}

function TimelineRow({
  entry,
  canInspectRuns,
  isSubtaskEntry,
  isLast,
  userNameById,
  agentNameById,
}: {
  entry: TaskTimelineEntry;
  canInspectRuns: boolean;
  isSubtaskEntry: boolean;
  isLast: boolean;
  userNameById: Map<string, string>;
  agentNameById: Map<string, string>;
}) {
  const gt = useGT();
  const Icon = Object.hasOwn(KIND_ICONS, entry.kind)
    ? KIND_ICONS[entry.kind]
    : GearSixIcon;
  const actor =
    recordedTimelineAgentName(entry) ??
    actorLabel(entry.actor, userNameById, agentNameById, gt);

  return (
    <li className="flex gap-2.5" data-kind={entry.kind}>
      <div className="flex flex-col items-center">
        <span
          className={cn(
            'mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full border',
            entry.actor.type === 'user' &&
              'border-[oklch(0.86_0.07_240)] bg-[oklch(0.96_0.03_240)] text-[oklch(0.42_0.13_250)]',
            entry.actor.type === 'agent' &&
              'border-[oklch(0.86_0.06_290)] bg-[oklch(0.96_0.03_290)] text-[oklch(0.44_0.14_290)]',
            entry.actor.type === 'workflow' &&
              'border-[oklch(0.86_0.07_160)] bg-[oklch(0.96_0.03_160)] text-[oklch(0.4_0.1_160)]',
            entry.actor.type === 'system' &&
              'border-stone-200 bg-stone-50 text-stone-500',
          )}
          title={actor}
        >
          <Icon className="size-3.5" />
        </span>
        {!isLast && <span className="w-px flex-1 bg-border" aria-hidden />}
      </div>
      <div className="min-w-0 flex-1 pb-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5 text-xs leading-5">
            <span className="inline-flex items-center gap-1 font-medium text-stone-900">
              <ActorIcon actor={entry.actor} />
              {entry.actor.type === 'workflow' && entry.actor.workflowId ? (
                <Link
                  to="/workflows/$workflowId/runs/$workflowRunId"
                  params={{
                    workflowId: entry.actor.workflowId,
                    workflowRunId: entry.actor.workflowRunId,
                  }}
                  className="hover:underline"
                >
                  {actor}
                </Link>
              ) : (
                actor
              )}
            </span>
            <EntrySummary
              entry={entry}
              userNameById={userNameById}
              agentNameById={agentNameById}
            />
            {isSubtaskEntry && (
              <span className="inline-flex items-center rounded bg-stone-100 px-1 py-px text-[10px] leading-4 text-stone-500">
                <T context="badge marking a timeline entry from a subtask">
                  subtask
                </T>
              </span>
            )}
          </div>
          <time
            className="shrink-0 whitespace-nowrap pt-px text-[11px] leading-5 text-muted-foreground"
            dateTime={entry.timestamp.toISOString()}
            title={formatTimelineAbsolute(entry.timestamp)}
          >
            {formatTimelineRelative(entry.timestamp)}
          </time>
        </div>
        <EntryDetail entry={entry} canInspectRuns={canInspectRuns} />
      </div>
    </li>
  );
}

function formatAssigneeSnapshot(
  snapshot: {
    assigneeType: string | null;
    assigneeUserId: string | null;
    assigneeAgentDefinitionId: string | null;
  },
  userNameById: Map<string, string>,
  agentNameById: Map<string, string>,
  gt: ReturnType<typeof useGT>,
): string {
  if (snapshot.assigneeUserId) {
    return (
      userNameById.get(snapshot.assigneeUserId) ??
      gt('User', {
        context: 'timeline assignee fallback when user name missing',
      })
    );
  }
  if (snapshot.assigneeAgentDefinitionId) {
    return (
      agentNameById.get(snapshot.assigneeAgentDefinitionId) ??
      gt('Agent', {
        context: 'timeline assignee fallback when agent name missing',
      })
    );
  }
  if (snapshot.assigneeType === 'user') {
    return gt('User', {
      context: 'timeline assignee when type is user but id missing',
    });
  }
  if (snapshot.assigneeType === 'agent') {
    return gt('Agent', {
      context: 'timeline assignee when type is agent but id missing',
    });
  }
  return gt('Unassigned', {
    context: 'timeline assignee when nobody is assigned',
  });
}

/** One-line summary phrase per entry kind. */
function EntrySummary({
  entry,
  userNameById,
  agentNameById,
}: {
  entry: TaskTimelineEntry;
  userNameById: Map<string, string>;
  agentNameById: Map<string, string>;
}) {
  const gt = useGT();
  switch (entry.kind) {
    case 'task_created':
      return (
        <span className="inline-flex flex-wrap items-center gap-x-1 text-stone-700">
          <T context="timeline: a task came into existence">created task</T>
          <span className="font-medium">{entry.title}</span>
        </span>
      );
    case 'task_status_changed':
      return (
        <span className="inline-flex flex-wrap items-center gap-x-1 text-stone-700">
          <T context="timeline: a task status transition">changed status</T>
          <StatusToken value={entry.previousStatus} />
          <ArrowRightIcon className="size-3 shrink-0 text-stone-400" />
          <StatusToken value={entry.status} />
        </span>
      );
    case 'task_assignee_changed':
      return (
        <span className="inline-flex flex-wrap items-center gap-x-1 text-stone-700">
          <T context="timeline: a task assignee change">changed assignee</T>
          <StatusToken
            value={formatAssigneeSnapshot(
              {
                assigneeType: entry.previousAssigneeType,
                assigneeUserId: entry.previousAssigneeUserId,
                assigneeAgentDefinitionId:
                  entry.previousAssigneeAgentDefinitionId,
              },
              userNameById,
              agentNameById,
              gt,
            )}
          />
          <ArrowRightIcon className="size-3 shrink-0 text-stone-400" />
          <StatusToken
            value={formatAssigneeSnapshot(
              {
                assigneeType: entry.assigneeType,
                assigneeUserId: entry.assigneeUserId,
                assigneeAgentDefinitionId: entry.assigneeAgentDefinitionId,
              },
              userNameById,
              agentNameById,
              gt,
            )}
          />
        </span>
      );
    case 'task_concluded':
      return (
        <span className="inline-flex flex-wrap items-center gap-x-1 text-stone-700">
          <T context="timeline: a task reached a terminal status">
            concluded task
          </T>
          <StatusToken value={entry.status} />
        </span>
      );
    case 'human_action':
      return (
        <span className="inline-flex flex-wrap items-center gap-x-1 text-stone-700">
          <HumanActionSummary actionKey={entry.actionKey} />
        </span>
      );
    case 'workflow_step_run':
      return (
        <span className="inline-flex flex-wrap items-center gap-x-1 text-stone-700">
          <T context="timeline: a workflow step ran against the task">
            ran workflow step
          </T>
          <span className="font-medium">
            {entry.stepName ?? entry.stepType ?? entry.stepRunId}
          </span>
          <StatusToken value={entry.status} />
        </span>
      );
    case 'agent_run':
      if (entry.runSummary) return <TaskRunOutcomeBadge entry={entry} />;
      return (
        <span className="inline-flex flex-wrap items-center gap-x-1 text-stone-700">
          <T context="timeline: an AI agent run worked the task">agent run</T>
          <StatusToken value={entry.status} />
        </span>
      );
    case 'ai_reasoning':
      return (
        <span className="inline-flex flex-wrap items-center gap-x-1 text-stone-700">
          <T context="timeline: an AI decision record">AI decision</T>
          <code className="rounded bg-stone-100 px-1 py-px text-[11px] leading-4">
            {entry.action}
          </code>
          <span className="text-[11px] leading-4 text-muted-foreground">
            {entry.confidence}
          </span>
        </span>
      );
    case 'task_deleted':
      return (
        <span className="inline-flex items-center text-stone-700">
          <T context="timeline: someone soft-deleted the task">deleted task</T>
        </span>
      );
    case 'task_snooze':
      return (
        <span className="inline-flex flex-wrap items-center gap-x-1 text-stone-700">
          <TaskSnoozeSummary entry={entry} />
        </span>
      );
    case 'operator_step':
      return (
        <span className="inline-flex flex-wrap items-center gap-x-1 text-stone-700">
          {entry.actionTools?.length ? (
            <span className="break-words">
              {entry.actionTools
                .map((tool) => tool.replaceAll('_', ' '))
                .join(' + ')}
            </span>
          ) : (
            <T context="timeline: a computer-use operator step">
              operator step
            </T>
          )}
          <span className="tabular-nums">#{entry.stepIndex}</span>
          <StatusToken value={entry.status} />
        </span>
      );
    default:
      return (
        <span className="inline-flex items-center text-stone-700">
          <T context="timeline: unrecognized entry kind from the server">
            recorded activity
          </T>
        </span>
      );
  }
}

/** Expanded per-kind detail under the summary line, when there is any. */
function EntryDetail({
  entry,
  canInspectRuns,
}: {
  entry: TaskTimelineEntry;
  canInspectRuns: boolean;
}): ReactNode {
  switch (entry.kind) {
    case 'task_status_changed':
    case 'task_concluded':
      return <TaskConclusionDetail entry={entry} />;
    case 'human_action':
      return (
        <HumanActionDetail
          actionKey={entry.actionKey}
          payload={entry.payload}
        />
      );
    case 'workflow_step_run':
      return <EntryText text={entry.toolType} />;
    case 'agent_run':
      return <AgentRunDetail entry={entry} canInspectRuns={canInspectRuns} />;
    case 'ai_reasoning':
      return <EntryText text={entry.reasoning} className="line-clamp-3" />;
    case 'task_snooze':
      return <EntryText text={entry.note} />;
    case 'operator_step':
      return (
        <div className="mt-1 flex flex-col items-start gap-1">
          <TaskRunEvidenceButton
            agentRunId={entry.agentRunId}
            agentName={entry.agentName}
            operatorStepId={entry.operatorStepId}
            allowed={canInspectRuns}
          />
          <details className="w-full text-xs text-muted-foreground">
            <summary className="cursor-pointer">
              <T context="task timeline original operator event disclosure">
                Event Details
              </T>
            </summary>
            <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-all">
              {JSON.stringify(entry, null, 2)}
            </pre>
          </details>
        </div>
      );
    default:
      return null;
  }
}

function EntryText({
  text,
  className,
}: {
  text: string | null;
  className?: string;
}) {
  if (!text) return null;
  return (
    <div className={cn('mt-0.5 text-[11px] text-muted-foreground', className)}>
      {text}
    </div>
  );
}

function AgentRunDetail({
  entry,
  canInspectRuns,
}: {
  entry: Extract<TaskTimelineEntry, { kind: 'agent_run' }>;
  canInspectRuns: boolean;
}) {
  if (entry.runSummary)
    return <TaskRunSummary entry={entry} canInspectRuns={canInspectRuns} />;
  return <EntryText text={entry.error} className="text-[oklch(0.5_0.16_25)]" />;
}

function TaskSnoozeSummary({
  entry,
}: {
  entry: Extract<TaskTimelineEntry, { kind: 'task_snooze' }>;
}) {
  const gt = useGT();
  const when = entry.snoozedUntil
    ? format(entry.snoozedUntil, 'MMM d, h:mm a')
    : '';
  if (entry.reason === 'snooze') {
    return gt('snoozed until {when}', {
      context: 'timeline: someone snoozed the task until a time',
      when,
    });
  }
  if (entry.reason === 'clear') {
    return gt('cleared the snooze', {
      context: 'timeline: someone cleared a task snooze',
    });
  }
  if (entry.reason === 'assignee_changed') {
    return gt('snooze cleared because the assignee changed', {
      context: 'timeline: changing the assignee cleared the snooze',
    });
  }
  if (entry.reason === 'needs_input') {
    return gt('snooze cleared because the task needs input', {
      context: 'timeline: the task needing input cleared the snooze',
    });
  }
  return gt('snooze cleared because the task was edited', {
    context: 'timeline: editing the task cleared the snooze',
  });
}

function HumanActionSummary({ actionKey }: { actionKey: string }) {
  if (actionKey === AGENT_HELP_REQUESTED_ACTION_KEY) {
    return (
      <T context="timeline: an AI agent asked a human a question on this task">
        asked for input
      </T>
    );
  }
  if (actionKey === HUMAN_INPUT_PROVIDED_ACTION_KEY) {
    return (
      <T context="timeline: a human answered an agent help request on this task">
        replied to the agent
      </T>
    );
  }
  if (actionKey === TASK_RETURNED_TO_AGENT_ACTION_KEY) {
    return (
      <T context="timeline: the chat agent re-queued the task for the agent that escalated it">
        handed the task back
      </T>
    );
  }
  if (actionKey === PROCEDURE_LOCK_RELEASED_ACTION_KEY) {
    return (
      <T context="timeline: a human released the procedure version the task was locked to">
        switched to the latest procedure version
      </T>
    );
  }
  return (
    <>
      <T context="timeline: a recorded human action on the task">
        performed action
      </T>
      <code className="rounded bg-stone-100 px-1 py-px text-[11px] leading-4">
        {actionKey}
      </code>
    </>
  );
}

function TaskConclusionDetail({
  entry,
}: {
  entry: Extract<
    TaskTimelineEntry,
    { kind: 'task_status_changed' | 'task_concluded' }
  >;
}) {
  return (
    <div className="mt-0.5 text-[11px] text-muted-foreground">
      {entry.outcome && (
        <p>
          <T context="timeline detail label">Outcome:</T>{' '}
          <span className="capitalize text-stone-700">{entry.outcome}</span>
        </p>
      )}
      {entry.outcomeReason && <p>{entry.outcomeReason}</p>}
      {'sourceDetail' in entry && entry.sourceDetail && (
        <p>{entry.sourceDetail}</p>
      )}
      <TaskOutcomeDataDisplay
        data={entry.outcomeData ?? null}
        fields={entry.outcomeContractSnapshot?.fields ?? []}
        outcome={entry.outcome ?? null}
      />
    </div>
  );
}

function HumanActionDetail({
  actionKey,
  payload,
}: {
  actionKey: string;
  payload: Record<string, unknown> | null;
}) {
  if (actionKey === OUTCOME_VALIDATION_FAILED_ACTION_KEY)
    return (
      <div>
        <p className="font-medium text-amber-700">
          <T context="task outcome review">
            Unvalidated agent response — verify evidence before retrying
          </T>
        </p>
        <JsonPayload payload={payload ?? {}} />
      </div>
    );
  const view = describeHumanAction(actionKey, payload);
  switch (view.kind) {
    case 'none':
      return null;
    case 'help_request':
      return <HelpRequestDetail view={view} />;
    case 'reply':
      return (
        <p className="mt-0.5 whitespace-pre-wrap text-[11px] leading-4 text-stone-600">
          {view.response}
        </p>
      );
    case 'returned_to_agent':
      return <ReturnedToAgentDetail view={view} />;
    case 'procedure_lock_released':
      return (
        <p className="mt-0.5 text-[11px] leading-4 text-stone-600">
          <T context="timeline detail: which procedure lock was released and its old version">
            Released <Var>{view.name}</Var> v<Var>{view.releasedVersion}</Var>.
            The next attempt uses the latest version.
          </T>
        </p>
      );
    case 'json':
      return <JsonPayload payload={view.payload} />;
  }
}

function ReturnedToAgentDetail({
  view,
}: {
  view: Extract<HumanActionDetailView, { kind: 'returned_to_agent' }>;
}) {
  return (
    <p className="mt-0.5 whitespace-pre-wrap text-[11px] leading-4 text-stone-600">
      <T context="timeline label for the note the chat agent left when handing a task back to the agent that escalated it">
        Handed back to the agent:
      </T>{' '}
      {view.note}
    </p>
  );
}

function HelpRequestDetail({
  view,
}: {
  view: Extract<HumanActionDetailView, { kind: 'help_request' }>;
}) {
  return (
    <div className="mt-0.5 space-y-0.5 text-[11px] leading-4 text-stone-600">
      {view.question && <p className="whitespace-pre-wrap">{view.question}</p>}
      {view.context && (
        <p className="whitespace-pre-wrap text-muted-foreground">
          {view.context}
        </p>
      )}
      {view.attempted && (
        <p className="whitespace-pre-wrap text-muted-foreground">
          <T context="timeline label for what the agent already tried">
            Already attempted:
          </T>{' '}
          {view.attempted}
        </p>
      )}
    </div>
  );
}

function JsonPayload({ payload }: { payload: Record<string, unknown> }) {
  return (
    <pre className="mt-0.5 max-h-24 overflow-auto rounded bg-stone-50 p-1.5 text-[10.5px] text-stone-600">
      {JSON.stringify(payload, null, 2)}
    </pre>
  );
}

/** Raw status/vocabulary token — data values, rendered untranslated. */
function StatusToken({ value }: { value: string }) {
  return (
    <span className="inline-flex items-center rounded bg-stone-100 px-1 py-px text-[11px] leading-4 text-stone-700">
      {value}
    </span>
  );
}
